import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { useGoogleMaps } from '../contexts/GoogleMapsContext';
import { Spin, Alert, Tooltip, message, Badge, Card, Button, DatePicker } from 'antd';
import { DollarOutlined, CalendarOutlined, FolderOutlined, LineChartOutlined, SaveOutlined, SyncOutlined } from '@ant-design/icons';
import moment from 'moment';
import './RouteMap.css';

const JOB_MARKER_COLOR = '#1890ff';
const SIDEBAR_WIDTH = 340;

const RouteMap = () => {
  const { isLoaded, loadError, googleMapsReady } = useGoogleMaps();
  const [companyAddress, setCompanyAddress] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  const [markers, setMarkers] = useState([]); // [{ position, title, isBusiness }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => moment().startOf('day'));
  const [showCalendar, setShowCalendar] = useState(false);
  const [jobDays, setJobDays] = useState({}); // Mapping of YYYY-MM-DD to job count
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [jobs, setJobs] = useState([]); // Jobs for selected day
  const [directions, setDirections] = useState(null);
  const [mapObj, setMapObj] = useState(null);
  const [highlightedJobIdx, setHighlightedJobIdx] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [optimizedMarkers, setOptimizedMarkers] = useState(null);
  const [hasSavedRoute, setHasSavedRoute] = useState(false);
  const [loadingSavedRoute, setLoadingSavedRoute] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [routeName, setRouteName] = useState('');

  // Debug: log when selectedDate changes
  useEffect(() => {
    console.debug('SelectedDate changed:', selectedDate?.format('YYYY-MM-DD') || 'No date selected');
  }, [selectedDate]);

  // Fetch business address on mount
  useEffect(() => {
    const fetchAddress = async () => {
      setLoading(true);
      setError(null);
      try {
        const profileResponse = await axiosInstance.get('/profile');
        const data = profileResponse.data;
        const address =
          data.businessInfo?.address ||
          data.profile?.businessInfo?.address ||
          data.user?.profile?.businessInfo?.address;
        if (address) {
          setCompanyAddress(address);
        } else {
          setError('No business address found. Please set it in your profile.');
        }
      } catch (err) {
        setError('Failed to load business address.');
      } finally {
        setLoading(false);
      }
    };
    fetchAddress();
  }, []);

  // Geocode business address when ready
  useEffect(() => {
    if (!companyAddress || !googleMapsReady || !window.google) return;
    setLoading(true);
    setError(null);
    if (!geocoder) setGeocoder(new window.google.maps.Geocoder());
    if (!geocoder) return;
    geocoder.geocode({ address: companyAddress }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results.length > 0) {
        const location = results[0].geometry.location;
        const pos = { lat: location.lat(), lng: location.lng() };
        setMapCenter(pos);
        setMarkers([{ position: pos, title: 'Business Address', isBusiness: true }]);
        setLoading(false);
      } else {
        setError('Could not geocode business address.');
        setLoading(false);
      }
    });
  }, [companyAddress, googleMapsReady, geocoder]);

  // Fetch job days for the visible month
  useEffect(() => {
    if (!selectedDate) return;
    const startOfMonth = moment(selectedDate).startOf('month').format('YYYY-MM-DD');
    const endOfMonth = moment(selectedDate).endOf('month').format('YYYY-MM-DD');
    console.debug('Fetching calendar events for month:', startOfMonth, 'to', endOfMonth);
    setCalendarLoading(true);
    axiosInstance.get(`/calendar/events?start=${startOfMonth}&end=${endOfMonth}`)
      .then(res => {
        console.debug('Received calendar events:', res.data);
        // Collect unique days with jobs
        const days = {};
        (res.data || []).forEach(event => {
          if (event.start) {
            const day = moment(event.start).format('YYYY-MM-DD');
            days[day] = days[day] ? days[day] + 1 : 1;
          }
        });
        console.debug('Job day counts:', days);
        setJobDays(days); // { 'YYYY-MM-DD': count }
      })
      .catch(() => setJobDays({}))
      .finally(() => setCalendarLoading(false));
  }, [selectedDate]);

  // Fetch jobs for the selected day
  useEffect(() => {
    console.debug('Initiating fetchJobs for date:', selectedDate?.format('YYYY-MM-DD'));
    if (!selectedDate || !googleMapsReady || !window.google || !geocoder || !companyAddress) return;
    setLoading(true);
    setError(null);
    
    // Clear any existing route data when date changes
    setDirections(null);
    setOptimizedRoute(null);
    setOptimizedMarkers(null);
    setHasSavedRoute(false);
    
    const fetchJobs = async () => {
      try {
        console.debug('Requesting jobs for date:', selectedDate.format('YYYY-MM-DD'));
        // Ensure we have a valid date for the API request
        const dateStr = selectedDate?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD');
        const res = await axiosInstance.get(`/calendar/events?date=${dateStr}`);
        console.debug('Received jobs response:', res.data);
        const jobs = res.data || [];
        setJobs(jobs);
        
        // Reset markers to just business address if no jobs
        if (jobs.length === 0) {
          // Keep only business marker
          setMarkers(prev => {
            const business = prev.find(m => m.isBusiness);
            return business ? [business] : [];
          });
          setDirections(null);
          message.info('No jobs for this day', 2);
          setLoading(false);
          return;
        }
        
        // Geocode all job addresses
        const jobMarkers = [];
        for (const [idx, jobEvent] of jobs.entries()) {
          const address = jobEvent.job?.customer?.address || jobEvent.job?.location?.address;
          if (!address) continue;
          // eslint-disable-next-line no-loop-func
          await new Promise((resolve) => {
            geocoder.geocode({ address }, (results, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && results.length > 0) {
                const location = results[0].geometry.location;
                jobMarkers.push({
                  position: { lat: location.lat(), lng: location.lng() },
                  title: jobEvent.job?.service || 'Job',
                  time: jobEvent.start,
                  address,
                  isBusiness: false,
                  idx
                });
              }
              resolve();
            });
          });
        }
        // Keep business marker at the top
        setMarkers(prev => {
          const business = prev.find(m => m.isBusiness);
          return business ? [business, ...jobMarkers] : jobMarkers;
        });
        
        // After jobs are loaded, check for saved optimized route
        try {
          setLoadingSavedRoute(true);
          const savedRouteResponse = await axiosInstance.get(`/routes/optimized-by-date/${dateStr}`);
          if (savedRouteResponse.data && savedRouteResponse.data.success) {
            console.log('Found saved optimized route:', savedRouteResponse.data);
            const savedRoute = savedRouteResponse.data.route;
            
            // Set flag that we have a saved route
            setHasSavedRoute(true);
            
            // Process the saved route data
            if (savedRoute.optimizationData) {
              // Apply the saved optimization
              const optimizedData = savedRoute.optimizationData;
              setOptimizedRoute({ 
                ...optimizedData,
                success: true, 
                isSavedRoute: true,
                routeId: savedRoute._id 
              });
              
              // Get the optimized waypoint order
              const optimizedOrder = optimizedData.waypoint_order || [];
              
              if (optimizedOrder && Array.isArray(optimizedOrder)) {
                // Reorder markers based on optimization
                const businessMarker = markers.find(m => m.isBusiness);
                if (businessMarker && jobMarkers.length > 0) {
                  const newOrderedMarkers = [];
                  
                  // First add business marker (start point)
                  newOrderedMarkers.push(businessMarker);
                  
                  // Add job markers in optimized order
                  optimizedOrder.forEach(idx => {
                    if (idx >= 0 && idx < jobMarkers.length) {
                      newOrderedMarkers.push(jobMarkers[idx]);
                    }
                  });
                  
                  // Add business marker again at the end (return point)
                  newOrderedMarkers.push(businessMarker);
                  
                  // Update markers in the optimized order
                  setOptimizedMarkers(newOrderedMarkers);
                  
                  // Reorder jobs based on the optimization
                  if (jobs.length > 0 && optimizedOrder.length > 0) {
                    const reorderedJobs = [];
                    optimizedOrder.forEach(idx => {
                      if (idx >= 0 && idx < jobs.length) {
                        reorderedJobs.push(jobs[idx]);
                      }
                    });
                    
                    if (reorderedJobs.length > 0) {
                      setJobs(reorderedJobs);
                    }
                  }
                  
                  message.success('Loaded saved optimized route', 2);
                }
              }
            }
          }
        } catch (routeError) {
          // It's okay if we don't find a saved route - just log, don't show error
          console.log('No saved route found for this date:', routeError);
          setHasSavedRoute(false);
        } finally {
          setLoadingSavedRoute(false);
        }
        
      } catch (err) {
        setError('Failed to load jobs for selected day.');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [selectedDate, googleMapsReady, geocoder, companyAddress]);

  // Compute and render route directions when map or markers change
  useEffect(() => {
    if (!mapObj || markers.length < 2) {
      setDirections(null);
      return;
    }
    const directionsService = new window.google.maps.DirectionsService();
    const origin = markers[0].position;
    const destination = markers[markers.length - 1].position;
    const waypoints = markers.slice(1, markers.length - 1).map(m => ({ location: m.position, stopover: true }));
    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK || status === 'OK') {
          setDirections(result);
        } else {
          console.error('Directions request failed due to', status);
        }
      }
    );
  }, [mapObj, markers]);

  // Sidebar job card click/hover highlight logic
  const handleJobCardHover = (idx) => setHighlightedJobIdx(idx);
  const handleJobCardLeave = () => setHighlightedJobIdx(null);

  // Handle route optimization
  const handleOptimizeRoute = async () => {
    if (markers.length < 3) { // Need at least business + 2 stops to optimize
      message.info('Need at least 2 stops to optimize a route');
      return;
    }

    setOptimizing(true);
    try {
      // Create waypoints array for API
      // First marker is business address (start/end point)
      const businessMarker = markers.find(m => m.isBusiness);
      if (!businessMarker) {
        throw new Error('Business address not found');
      }

      // Get all job markers
      const jobMarkers = markers.filter(m => !m.isBusiness);
      
      // Check if we have too many markers for Google Maps API (25 max)
      if (jobMarkers.length > 25) {
        message.error('Google Maps API only supports up to 25 waypoints for optimization');
        setOptimizing(false);
        return;
      }
      
      // Create waypoints for API
      const waypoints = [
        // Start at business address
        {
          id: "business_start",
          point: {
            lat: businessMarker.position.lat,
            lng: businessMarker.position.lng
          }
        },
        // Add all job locations
        ...jobMarkers.map((marker, idx) => ({
          id: `job_${marker.idx || idx}`,
          point: {
            lat: marker.position.lat,
            lng: marker.position.lng
          }
        })),
        // End at business address again
        {
          id: "business_end",
          point: {
            lat: businessMarker.position.lat,
            lng: businessMarker.position.lng
          }
        }
      ];

      // Call backend to optimize route
      const response = await axiosInstance.post('/routes/optimize', {
        waypoints,
        date: selectedDate.format('YYYY-MM-DD')
      });

      console.log('Optimization response:', response.data);

      // Handle successful optimization
      if (response.data && response.data.success) {
        const optimizedData = response.data;
        setOptimizedRoute(optimizedData);
        
        // Get the optimized waypoint order from the response
        const optimizedOrder = optimizedData.optimizedOrder || [];
        
        if (!optimizedOrder || !Array.isArray(optimizedOrder)) {
          throw new Error('Invalid optimization solution structure');
        }

        // Reorder markers based on optimization
        const newOrderedMarkers = [];
        
        // First add business marker (start point)
        newOrderedMarkers.push(businessMarker);
        
        // Add job markers in optimized order
        optimizedOrder.forEach(idx => {
          if (idx >= 0 && idx < jobMarkers.length) {
            newOrderedMarkers.push(jobMarkers[idx]);
          }
        });
        
        // Add business marker again at the end (return point)
        newOrderedMarkers.push(businessMarker);
        
        console.log('Reordered markers:', newOrderedMarkers);
        
        // Update markers in the optimized order
        setOptimizedMarkers(newOrderedMarkers);
        
        // Reorder jobs based on the optimization sequence
        if (optimizedOrder.length > 0) {
          const reorderedJobs = [];
          
          // Create new order of jobs based on the optimization
          optimizedOrder.forEach(idx => {
            if (idx >= 0 && idx < jobs.length) {
              reorderedJobs.push(jobs[idx]);
            }
          });
          
          // If we found some jobs, update the jobs array
          if (reorderedJobs.length > 0) {
            setJobs(reorderedJobs);
          }
        }
        
        message.success('Route optimized successfully!');
      } else {
        throw new Error(response.data?.message || 'Invalid response from route optimization service');
      }
    } catch (err) {
      console.error('Error optimizing route:', err);
      message.error(`Failed to optimize route: ${err.message}`);
    } finally {
      setOptimizing(false);
    }
  };

  // Use optimized markers if available, otherwise use original markers
  useEffect(() => {
    if (optimizedMarkers) {
      // Show optimized route
      if (mapObj && optimizedMarkers.length >= 2) {
        const directionsService = new window.google.maps.DirectionsService();
        const origin = optimizedMarkers[0].position;
        const destination = optimizedMarkers[optimizedMarkers.length - 1].position;
        const waypoints = optimizedMarkers.slice(1, optimizedMarkers.length - 1).map(m => ({ 
          location: m.position, 
          stopover: true 
        }));
        
        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK || status === 'OK') {
              setDirections(result);
            } else {
              console.error('Directions request failed due to', status);
            }
          }
        );
      }
    }
  }, [mapObj, optimizedMarkers]);

  // Handle saving the current route
  const handleSaveRoute = async () => {
    if (!optimizedRoute) {
      message.error('No route to save. Please optimize the route first.');
      return;
    }

    setSavingRoute(true);
    try {
      // We don't need to save if it's already a saved route
      if (optimizedRoute.isSavedRoute) {
        message.success('Route is already saved');
        setHasSavedRoute(true);
        setSavingRoute(false);
        return;
      }

      // Update the route data with a name
      const name = routeName || `Route for ${selectedDate.format('MMM D, YYYY')}`;
      
      // If we have a route ID, update it
      if (optimizedRoute.route) {
        const response = await axiosInstance.put(`/routes/${optimizedRoute.route}`, {
          name,
          isOptimized: true,
          lastOptimized: new Date()
        });
        
        if (response.data && response.data.success) {
          message.success('Route updated successfully');
          setHasSavedRoute(true);
        }
      } else {
        // If no ID, it's a new route being saved for the first time
        message.success('Route saved successfully');
        setHasSavedRoute(true);
      }
    } catch (error) {
      console.error('Error saving route:', error);
      message.error('Failed to save route');
    } finally {
      setSavingRoute(false);
    }
  };

  if (loadError) {
    return <Alert message="Error loading Google Maps" description={loadError.message} type="error" showIcon style={{ margin: 32 }} />;
  }
  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon style={{ margin: 32 }} />;
  }
  if (loading || !isLoaded || !googleMapsReady) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}><Spin size="large" tip="Loading map..." /></div>;
  }

  return (
    <div style={{ maxWidth: 1400, margin: '40px auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Top Controls Container */}
      <div style={{ 
        display: 'flex', 
        gap: 16, 
        alignItems: 'stretch',
        position: 'relative'
      }}>
        {/* Left Section - Misc Component & Date Picker */}
        <Card
          style={{
            background: '#181f2a',
            borderRadius: 16,
            boxShadow: '0 4px 24px #0006',
            width: 200,
            border: '1px solid #232b3a',
            display: 'flex',
            flexDirection: 'column',
            padding: 0
          }}
        >
          {/* Misc Component Header */}
          <div style={{ 
            padding: 16, 
            borderBottom: '1px solid #232b3a',
            fontWeight: 600,
            fontSize: 16,
            color: '#fff'
          }}>
            Misc Comp
          </div>
          
          {/* Date Picker */}
          <div style={{ padding: 16 }}>
            <div style={{ 
              color: '#b5b5b5',
              fontSize: 14,
              marginBottom: 8,
              fontWeight: 500
            }}>
              DATE PICKER
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 12 
            }}>
              {/* Basic Date Picker */}
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  // Only update on actual selection, not hover
                  if (date) {
                    console.debug('DatePicker explicit selection:', date.format('YYYY-MM-DD'));
                    setSelectedDate(date);
                  }
                }}
                // Prevent automatic showing of the picker panel
                open={false}
                // Only open when explicitly clicked, not on hover
                onClick={() => {
                  console.debug('DatePicker clicked');
                }}
                // Clear hover-related handlers completely
                onMouseEnter={null}
                onMouseLeave={null}
                onPanelChange={null}
                // Disable additional features
                inputReadOnly={true}
                format="MM/DD/YYYY"
                style={{ 
                  width: '100%', 
                  pointerEvents: 'none' // This prevents hover events entirely
                }}
                // Create a focused handler for explicit clicks
                onFocus={() => {
                  console.debug('DatePicker focused');
                  // Replace the DatePicker with a fake input that opens a simple date picker
                  const customPicker = document.createElement('input');
                  customPicker.type = 'date';
                  customPicker.style.position = 'absolute';
                  customPicker.style.opacity = '0';
                  customPicker.style.height = '0';
                  customPicker.value = selectedDate.format('YYYY-MM-DD');
                  
                  customPicker.addEventListener('change', (e) => {
                    const newDate = moment(e.target.value);
                    setSelectedDate(newDate);
                    document.body.removeChild(customPicker);
                  });
                  
                  document.body.appendChild(customPicker);
                  customPicker.showPicker();
                }}
              />
              
              {/* Quick selection buttons */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <Button 
                  size="small" 
                  onClick={() => {
                    console.debug('Back button clicked, going back 1 day');
                    setSelectedDate(moment(selectedDate).subtract(1, 'day'));
                  }}
                >
                  Back
                </Button>
                <Button 
                  size="small" 
                  onClick={() => {
                    console.debug('Today button clicked');
                    setSelectedDate(moment());
                  }}
                >
                  Today
                </Button>
                <Button 
                  size="small" 
                  onClick={() => {
                    console.debug('Next button clicked, going forward 1 day');
                    setSelectedDate(moment(selectedDate).add(1, 'day'));
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </Card>
        
        {/* Center Section - Optimize Route */}
        <Card
          style={{
            background: '#181f2a',
            borderRadius: 16,
            boxShadow: '0 4px 24px #0006',
            width: 'calc(100% - 200px - 180px - 32px)',
            marginLeft: 0,
            border: '1px solid #232b3a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              color: '#fff', 
              fontWeight: 600, 
              marginBottom: 16,
              fontSize: 18
            }}>
              {hasSavedRoute ? 'Saved Route Loaded' : 'Optimize Route'}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Button 
                type="primary"
                size="large"
                onClick={handleOptimizeRoute}
                loading={optimizing}
                disabled={markers.length < 3}
                icon={hasSavedRoute ? <SyncOutlined /> : undefined}
              >
                {hasSavedRoute ? 'Re-Optimize' : 'Optimize'}
              </Button>
              
              {optimizedRoute && (
                <Button
                  type="default"
                  size="large"
                  onClick={handleSaveRoute}
                  loading={savingRoute}
                  icon={<SaveOutlined />}
                  disabled={hasSavedRoute}
                >
                  Save Route
                </Button>
              )}
            </div>
          </div>
        </Card>
        
        {/* Right Section - Route Management */}
        <Card
          style={{
            background: '#181f2a',
            borderRadius: 16,
            boxShadow: '0 4px 24px #0006',
            width: 180,
            border: '1px solid #232b3a',
            padding: 0
          }}
        >
          <div style={{ 
            padding: '12px 16px',
            borderBottom: '1px solid #232b3a',
            color: '#fff', 
            fontWeight: 600,
            fontSize: 16
          }}>
            Routes
          </div>
          <div style={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button 
                type="text" 
                style={{ 
                  textAlign: 'left', 
                  justifyContent: 'flex-start',
                  color: '#fff',
                  padding: '4px 8px'
                }}
                onClick={() => message.info('Saved Routes feature coming soon!')}
              >
                <FolderOutlined style={{ marginRight: 8 }} /> Saved Routes
              </Button>
              <Button 
                type="text" 
                style={{ 
                  textAlign: 'left', 
                  justifyContent: 'flex-start',
                  color: '#fff',
                  padding: '4px 8px'
                }}
                onClick={() => message.info('Route Analytics feature coming soon!')}
              >
                <LineChartOutlined style={{ marginRight: 8 }} /> Analytics
              </Button>
              <Button 
                type="text" 
                style={{ 
                  textAlign: 'left', 
                  justifyContent: 'flex-start',
                  color: '#fff',
                  padding: '4px 8px' 
                }}
                onClick={() => message.info('Create Template feature coming soon!')}
              >
                <SaveOutlined style={{ marginRight: 8 }} /> Save Template
              </Button>
            </div>
          </div>
        </Card>
      </div>
      {/* Map and Sidebar */}
      <div style={{ 
        display: 'flex', 
        gap: 24, 
        alignItems: 'flex-start',
        position: 'relative'
      }}>
        {/* Map */}
        <div style={{ flex: 1, minWidth: 400, height: '65vh', background: '#101624', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px #0008' }}>
          <GoogleMap
            mapContainerStyle={{ height: '100%', width: '100%' }}
            center={mapCenter}
            zoom={14}
            onLoad={map => setMapObj(map)}
            options={{
              fullscreenControl: true,
              mapTypeControl: true,
              streetViewControl: true,
              zoomControl: true,
              gestureHandling: 'greedy',
              scrollwheel: true,
              minZoom: 3,
              maxZoom: 20,
              clickableIcons: false,
              mapTypeId: 'roadmap',
            }}
          >
            {markers.map((marker, idx) => (
              <Marker
                key={idx}
                position={marker.position}
                title={marker.isBusiness
                  ? marker.title
                  : `${marker.title}${marker.time ? ` - ${moment(marker.time).format('h:mm A')}` : ''}`
                }
                icon={marker.isBusiness ? undefined : {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillColor: highlightedJobIdx === marker.idx ? '#fadb14' : JOB_MARKER_COLOR,
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: highlightedJobIdx === marker.idx ? '#fadb14' : '#fff',
                  scale: highlightedJobIdx === marker.idx ? 12 : 8
                }}
                zIndex={marker.isBusiness ? 1000 : (highlightedJobIdx === marker.idx ? 999 : 100)}
                onMouseOver={() => marker.idx !== undefined && setHighlightedJobIdx(marker.idx)}
                onMouseOut={() => marker.idx !== undefined && setHighlightedJobIdx(null)}
              />
            ))}
            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: { strokeColor: JOB_MARKER_COLOR, strokeWeight: 4 }
                }}
              />
            )}
          </GoogleMap>
        </div>
        {/* Sidebar */}
        <div style={{
          width: SIDEBAR_WIDTH,
          minHeight: '65vh',
          background: '#181f2a',
          borderRadius: 16,
          boxShadow: '0 2px 12px #0008',
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          overflowY: 'auto',
          maxHeight: '65vh',
        }}>
          <div style={{ color: '#fff', fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
            Jobs for {selectedDate?.format('MMM D, YYYY') || 'Today'}
          </div>
          {jobs.length === 0 ? (
            <Alert
              message="No jobs for this day"
              type="info"
              showIcon
              style={{ margin: '16px 0', background: '#232b3a', color: '#fff', border: 'none' }}
            />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: jobs.length > 6 ? '1fr 1fr' : '1fr',
              gap: 10,
            }}>
              {jobs.map((jobEvent, idx) => {
                const job = jobEvent.job || {};
                const customer = job.customer || {};
                const price = job.price || job.estimatedPrice || jobEvent.price || null;
                return (
                  <div
                    key={jobEvent._id || idx}
                    onMouseOver={() => handleJobCardHover(idx)}
                    onMouseOut={handleJobCardLeave}
                    style={{
                      background: highlightedJobIdx === idx ? '#22304a' : '#232b3a',
                      borderRadius: 10,
                      padding: '10px 12px 8px 12px',
                      boxShadow: highlightedJobIdx === idx ? '0 0 0 2px #fadb14' : '0 1px 4px #0006',
                      color: '#fff',
                      fontSize: 14,
                      cursor: 'pointer',
                      minWidth: 0,
                      position: 'relative',
                      transition: 'box-shadow 0.2s, background 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <div style={{ color: '#b5b5b5', fontSize: 12 }}>
                      {jobEvent.start ? moment(jobEvent.start).format('h:mm A') : 'No time'}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2, color: '#fff' }}>{job.service || 'Job'}</div>
                    <div style={{ color: '#8ecfff', fontWeight: 500, fontSize: 13 }}>{customer.name || 'Unknown Customer'}</div>
                    <div style={{ color: '#b5b5b5', fontSize: 12, marginBottom: 2, wordBreak: 'break-word' }}>{customer.address || job.location?.address || 'No address'}</div>
                    {price && (
                      <Badge
                        count={<span style={{ color: '#222', fontWeight: 700 }}>${price}</span>}
                        style={{ background: '#fadb14', color: '#222', position: 'absolute', bottom: 8, right: 8, boxShadow: '0 1px 4px #0006' }}
                        offset={[0, 0]}
                        size="small"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteMap;

